import { useEffect, useRef } from 'react'
import { Box, useTheme } from '@mui/material'
import BackendWakeBanner from '@components/BackendWakeBanner'
import OfflineStatusBar from '@components/offline/OfflineStatusBar'
import { useBackendWakeState } from '@/contexts/BackendWakeContext'
import { useShowBottomNav } from '@hooks/useShowBottomNav'
import { useShowMiniSessionBar } from '@hooks/useShowMiniSessionBar'
import { useShellOverlayMetrics } from './shellOverlayMetrics'
import { shellFixedBottomOffsetCss, shellOverlayZIndex } from './shellOverlayStack'

/** Fixed stack: wake banner above offline bar, both above mini-session + bottom nav. */
export function ShellBottomAlertStack() {
  const theme = useTheme()
  const showBottomNav = useShowBottomNav()
  const showMiniBar = useShowMiniSessionBar()
  const { showWakeBanner } = useBackendWakeState()
  const { keyboardInsetPx, setBannerHeightPx } = useShellOverlayMetrics()
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) {
      setBannerHeightPx(0)
      return undefined
    }

    const publish = () => setBannerHeightPx(el.getBoundingClientRect().height)

    if (typeof ResizeObserver === 'undefined') {
      publish()
      return undefined
    }

    const observer = new ResizeObserver(() => publish())
    observer.observe(el)
    publish()
    return () => {
      observer.disconnect()
      setBannerHeightPx(0)
    }
  }, [setBannerHeightPx, showWakeBanner])

  return (
    <Box
      ref={ref}
      data-testid="shell-bottom-alert-stack"
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: shellFixedBottomOffsetCss(
          { bottomNav: showBottomNav, miniSessionBar: showMiniBar },
          { keyboardInsetPx },
        ),
        zIndex: shellOverlayZIndex(theme).backendWakeBanner,
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: 'none',
        '& .MuiAlert-root, & .MuiButton-root, & button, [data-testid="backend-wake-banner"]': {
          pointerEvents: 'auto',
        },
      }}
    >
      <BackendWakeBanner visible={showWakeBanner} embedded />
      <OfflineStatusBar placement="stack" />
    </Box>
  )
}

export default ShellBottomAlertStack
